import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Paperclip, Trash2, Upload } from 'lucide-react';

import type { Attachment } from '@tasks/contracts';

import {
  attachmentDownloadUrl,
  deleteAttachment,
  listAttachments,
  uploadAttachment,
} from '../../api/attachments';
import { queryKeys } from '../../api/queryKeys';
import { Avatar } from '../../components/Avatar';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { IconButton } from '../../components/IconButton';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import { formatBytes, formatRelative } from '../../lib/format';
import styles from './IssueAttachmentsTab.module.css';

const MAX_SIZE_BYTES = 25 * 1024 * 1024;

/** UC-ATT-01/02: загрузка drag&drop, скачивание, удаление. */
export function IssueAttachmentsTab({ issueKey }: { readonly issueKey: string }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setDragging] = useState(false);
  const [toDelete, setToDelete] = useState<Attachment | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  const query = useQuery({
    queryKey: queryKeys.issues.attachments(issueKey),
    queryFn: ({ signal }) => listAttachments(issueKey, signal),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadAttachment(issueKey, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.issues.attachments(issueKey) });
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAttachment(id),
    onSuccess: () => {
      setToDelete(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.issues.attachments(issueKey) });
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  const handleFiles = (files: FileList | null): void => {
    const file = files?.[0];
    if (!file) {
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error('Файл больше 25 МБ', { code: 'PAYLOAD_TOO_LARGE' });
      return;
    }
    uploadMutation.mutate(file);
  };

  const attachments = query.data ?? [];

  return (
    <div className={styles['tab']}>
      <div
        className={isDragging ? `${styles['dropzone']} ${styles['dropzoneActive']}` : styles['dropzone']}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          handleFiles(event.dataTransfer.files);
        }}
      >
        <Upload aria-hidden size={18} />
        <p>Перетащите файл сюда или выберите вручную</p>
        <p className={styles['limit']}>Не больше 25 МБ</p>
        <Button size="sm" loading={uploadMutation.isPending} onClick={() => inputRef.current?.click()}>
          Загрузить файл
        </Button>
        <input
          ref={inputRef}
          type="file"
          hidden
          aria-label="Выбрать файл"
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = '';
          }}
        />
      </div>

      {query.isPending ? (
        <Skeleton variant="row" count={3} />
      ) : attachments.length === 0 ? (
        <EmptyState
          compact
          icon={<Paperclip size={18} />}
          title="Вложений нет"
          description="Загрузите первый файл — он появится в списке."
        />
      ) : (
        <ul className={styles['list']}>
          {attachments.map((attachment) => (
            <li key={attachment.id} className={styles['item']}>
              <Avatar user={attachment.uploader} size="xs" />
              <div className={styles['info']}>
                <a href={attachmentDownloadUrl(attachment.id)} className={styles['name']} download>
                  {attachment.filename}
                </a>
                <span className={styles['meta']}>
                  {formatBytes(attachment.sizeBytes)} · {formatRelative(attachment.createdAt)}
                </span>
              </div>
              <a
                href={attachmentDownloadUrl(attachment.id)}
                download
                className={styles['download']}
                aria-label={`Скачать ${attachment.filename}`}
              >
                <Download aria-hidden size={15} />
              </a>
              <IconButton
                label={`Удалить ${attachment.filename}`}
                icon={<Trash2 size={15} />}
                tone="danger"
                onClick={() => setToDelete(attachment)}
              />
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={toDelete !== null}
        title="Удалить вложение?"
        description="Файл будет удалён с сервера. Действие записывается в аудит."
        confirmLabel="Удалить"
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          if (toDelete) {
            deleteMutation.mutate(toDelete.id);
          }
        }}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
}
